import React from 'react';
import { createDebrief, fetchHiddenTruths, getSession, regenerateSession } from './api';
import { Brand, Transcript, formatTime } from './components';
import { hasHiddenTruths } from './content';
import { CopyDownload, FeedbackBody, TranscriptToggle } from './feedback';
import { compactTranscript, saveHistoryEntry } from './history';
import { ArrowIcon, SparkIcon } from './icons';
import { Markdown } from './markdown';
import { RubricDetails } from './Rubric';
import { tagTranscriptLines, type RolePlayPhase } from './roleplay';
import { ROLE_LABELS } from './scenarios';
import type { InterviewSetup, SessionRecord, TranscriptEntry, TranscriptLine } from './types';

export function toTranscriptLines(transcript: TranscriptEntry[]): TranscriptLine[] {
  return transcript.map((entry) => ({
    role: entry.role === 'interviewer' ? 'ai' : 'user',
    text: entry.text,
  }));
}

const POLL_MS = 10_000;
const SLOW_MS = 8 * 60_000;

/** Seconds spent waiting while `active`, ticking once a second. */
function useWaited(active: boolean): number {
  const [waited, setWaited] = React.useState(0);
  React.useEffect(() => {
    if (!active) return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(() => setWaited(Date.now() - startedAt), 1_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return waited;
}

type Feedback =
  | { status: 'empty' }
  | { status: 'generating' }
  | { status: 'failed'; message: string }
  | { status: 'done'; markdown: string };

/** Server sessions: poll until the background debrief is done. */
function useServerFeedback(sessionId: string, finishError: string) {
  const [record, setRecord] = React.useState<SessionRecord | null>(null);
  const [pollNonce, setPollNonce] = React.useState(0);

  React.useEffect(() => {
    if (!sessionId) return undefined;
    let stopped = false;
    let timer = 0;
    const tick = () => {
      getSession(sessionId)
        .then((next) => {
          if (stopped) return;
          setRecord(next);
          if (next.status === 'live' || next.status === 'debriefing') timer = window.setTimeout(tick, POLL_MS);
        })
        .catch(() => {
          if (!stopped) timer = window.setTimeout(tick, POLL_MS);
        });
    };
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [sessionId, pollNonce]);

  const feedback: Feedback = finishError
    ? { status: 'failed', message: finishError }
    : record?.status === 'done' && record.debriefMarkdown
      ? { status: 'done', markdown: record.debriefMarkdown }
      : record?.status === 'failed'
        ? { status: 'failed', message: record.error || 'The feedback could not be generated.' }
        : { status: 'generating' };

  const regenerate = () => {
    void regenerateSession(sessionId)
      .then((next) => {
        setRecord(next);
        setPollNonce((count) => count + 1);
      })
      .catch(() => undefined);
  };
  return { feedback, regenerate, serverStatus: record?.status ?? null };
}

/** Fallback when no server session exists: the direct, synchronous debrief call. */
function useDirectFeedback(
  enabled: boolean,
  args: { elapsed: number; notes: string; phaseById?: Record<string, RolePlayPhase>; sessionId: string; setup: InterviewSetup; transcript: TranscriptEntry[] },
) {
  const { elapsed, notes, phaseById, sessionId, setup, transcript } = args;
  const [feedback, setFeedback] = React.useState<Feedback>(
    transcript.length ? { status: 'generating' } : { status: 'empty' },
  );
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!enabled || transcript.length === 0) return undefined;
    const controller = new AbortController();
    setFeedback({ status: 'generating' });
    const lines =
      setup.scenario === 'roleplay_screening' && phaseById
        ? tagTranscriptLines(transcript, phaseById)
        : toTranscriptLines(transcript);
    createDebrief({ setup, transcript: lines, notes }, controller.signal)
      .then((response) => {
        saveHistoryEntry({
          id: sessionId || `local-${Date.now().toString(36)}`,
          date: new Date().toISOString(),
          scenario: setup.scenario,
          persona: setup.persona,
          candidateProfile: setup.candidateProfile,
          role: setup.role,
          durationMinutes: setup.durationMinutes,
          practisedMinutes: Math.max(1, Math.round(elapsed / 60_000)),
          debriefMarkdown: response.markdown,
          transcript: compactTranscript(setup, transcript),
        });
        setFeedback({ status: 'done', markdown: response.markdown });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setFeedback({ status: 'failed', message: error instanceof Error ? error.message : 'The debrief could not be written.' });
      });
    return () => controller.abort();
    // The transcript, notes and setup are frozen once the room has ended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, enabled]);

  return { feedback, regenerate: () => setAttempt((count) => count + 1), serverStatus: null as null };
}

export function Debrief({
  elapsed,
  finishError,
  notes,
  onPrep,
  onRestart,
  onRetryFinish,
  onSessions,
  phaseById,
  sessionId,
  setup,
  transcript,
}: {
  elapsed: number;
  /** Set when sending the transcript to the server failed. */
  finishError: string;
  notes: string;
  onPrep: () => void;
  onRestart: () => void;
  onRetryFinish: () => void;
  onSessions: () => void;
  phaseById?: Record<string, RolePlayPhase>;
  /** Empty when no server session could be created; the direct debrief call is used instead. */
  sessionId: string;
  setup: InterviewSetup;
  transcript: TranscriptEntry[];
}) {
  const serverMode = Boolean(sessionId);
  const server = useServerFeedback(sessionId, finishError);
  const direct = useDirectFeedback(!serverMode, { elapsed, notes, phaseById, sessionId, setup, transcript });
  const { feedback, regenerate, serverStatus } = serverMode ? server : direct;
  const [revealed, setRevealed] = React.useState(false);
  const [hidden, setHidden] = React.useState<{ status: 'idle' | 'loading' | 'error' | 'done'; text: string }>({ status: 'idle', text: '' });
  const canReveal = hasHiddenTruths(setup);
  const labels = ROLE_LABELS[setup.scenario];
  const userTurns = transcript.filter((entry) => entry.role === 'candidate').length;
  const aiEntries = transcript.filter((entry) => entry.role === 'interviewer');
  const rolePlay = setup.scenario === 'roleplay_screening' && Boolean(phaseById);
  const candidateTurns = rolePlay ? aiEntries.filter((entry) => phaseById?.[entry.id] === 2).length : 0;
  const aiTurns = aiEntries.length - candidateTurns;
  const waited = useWaited(feedback.status === 'generating');
  const markdown = feedback.status === 'done' ? feedback.markdown : '';

  return (
    <main className="debrief-page">
      <header className="app-header">
        <Brand compact />
        <div className="header-actions">
          <button className="text-button" onClick={onSessions} type="button">Sessions &amp; progress</button>
          <button className="text-button" onClick={onPrep} type="button">Prep pack</button>
          <span className="private-pill"><SparkIcon /> Session complete</span>
        </div>
      </header>
      <section className="debrief-layout">
        <div className="debrief-summary">
          <p className="eyebrow">Room closed</p>
          <h1>Good reps.<br /><em>Keep the signal.</em></h1>
          <p>Your written feedback is generated from the transcript and your private notes.</p>
          <div className="debrief-metrics">
            <div><strong>{formatTime(elapsed)}</strong><span>practice time</span></div>
            <div><strong>{userTurns}</strong><span>Your turns</span></div>
            <div><strong>{aiTurns}</strong><span>{rolePlay ? 'Tania' : labels.aiName} turns</span></div>
            {rolePlay && <div><strong>{candidateTurns}</strong><span>Candidate turns</span></div>}
          </div>
          <button className="primary-button primary-button--coral" onClick={onRestart} type="button">
            <span>Practice another round</span><ArrowIcon />
          </button>
        </div>
        <div className="debrief-content">
          <section className="feedback-card">
            <header>
              <span className="persona-monogram">AI</span>
              <div><p className="eyebrow">Feedback</p><h2>{setup.role}</h2></div>
            </header>
            {feedback.status === 'generating' && (
              <div className="generating-note" role="status">
                <span className="checking-line"><span /></span>
                <p>
                  {serverMode
                    ? serverStatus === 'live'
                      ? 'Sending your call to the server…'
                      : 'Writing your feedback on the server…'
                    : 'Tania is writing your debrief… ~1 min'}
                  {' '}<span className="waited">Waiting {formatTime(waited)}</span>
                </p>
                {serverMode && (
                  <p>
                    {waited > SLOW_MS
                      ? 'This is taking longer than usual — it will still appear in Sessions & progress when it is ready.'
                      : 'It can take about 5 minutes. You can close this page; the feedback will appear under Sessions & progress.'}
                  </p>
                )}
                {serverMode && (
                  <button className="text-button" onClick={onSessions} type="button">Open Sessions &amp; progress</button>
                )}
              </div>
            )}
            {feedback.status === 'empty' && <p>No conversation was captured, so there is nothing to give feedback on.</p>}
            {feedback.status === 'failed' && (
              <div className="form-error failed-box" role="alert">
                <p>{feedback.message}</p>
                <button className="primary-button primary-button--coral" onClick={finishError ? onRetryFinish : regenerate} type="button">
                  {finishError ? 'Resend for feedback' : 'Retry feedback'}
                </button>
              </div>
            )}
            {feedback.status === 'done' && <FeedbackBody markdown={feedback.markdown} />}
            {feedback.status === 'done' && <RubricDetails />}
            <div className="debrief-actions">
              {feedback.status === 'done' ? (
                <CopyDownload filename={`debrief-${setup.scenario}-${new Date().toISOString().slice(0, 10)}.md`} markdown={markdown} />
              ) : null}
              {(feedback.status === 'done') && (
                <button onClick={regenerate} type="button">Regenerate</button>
              )}
              {canReveal && (
                <button
                  disabled={hidden.status === 'loading'}
                  onClick={() => {
                    if (revealed) {
                      setRevealed(false);
                      return;
                    }
                    setRevealed(true);
                    if (hidden.status === 'done' || hidden.status === 'loading') return;
                    setHidden({ status: 'loading', text: '' });
                    fetchHiddenTruths(setup.candidateProfile)
                      .then((text) => setHidden({ status: 'done', text }))
                      .catch(() => setHidden({ status: 'error', text: '' }));
                  }}
                  type="button"
                >
                  {revealed ? 'Hide' : 'Reveal what the candidate was hiding'}
                </button>
              )}
            </div>
            {canReveal && revealed && (
              <div className="hidden-truths">
                <h3>What the candidate was hiding</h3>
                {hidden.status === 'loading' && <p role="status">Loading…</p>}
                {hidden.status === 'error' && <p role="alert">Could not load this. Click Hide, then Reveal to retry.</p>}
                {hidden.status === 'done' && <Markdown source={hidden.text || 'Nothing recorded for this profile.'} />}
              </div>
            )}
          </section>
          <TranscriptToggle count={transcript.length}>
            <Transcript
              emptyText="No transcript was captured."
              entries={transcript}
              labelFor={
                setup.scenario === 'roleplay_screening' && phaseById
                  ? (entry) => (entry.role === 'candidate' ? labels.user : phaseById[entry.id] === 2 ? 'Candidate' : 'Tania')
                  : undefined
              }
              labels={{ ai: labels.ai, user: labels.user }}
            />
          </TranscriptToggle>
        </div>
      </section>
    </main>
  );
}
